import { Logout, Person } from '@mui/icons-material';
import { AppBar, Box, IconButton, Toolbar, Tooltip, Typography } from '@mui/material';
import type { UserProfile } from '@shared/types';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router-dom';
import Login from './components/auth/Login';
import OAuthPopupCallback from './components/auth/OAuthPopupCallback';
import ConnectionStatus from './components/common/ConnectionStatus';
import { InstallPrompt } from './components/common/InstallPrompt';
import { LoadingAnimation } from './components/common/LoadingAnimation';
import VersionUpdateNotification from './components/common/VersionUpdateNotification';
import BottomToolbar from './components/dashboard/BottomToolbar';
import { ClothingSetManager } from './components/dashboard/ClothingSetManager';
import Dashboard from './components/dashboard/Dashboard';
import { DebugOverlay } from './components/debug/DebugOverlay';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import ProfileDrawer from './components/profile/ProfileDrawer';
import WorkoutDetail from './components/workout/WorkoutDetail';
import WorkoutLibrary from './components/workout/WorkoutLibrary';

import { useMsal } from '@azure/msal-react';
import EntraSignIn from './components/auth/EntraSignIn';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DebugProvider, useDebug } from './contexts/DebugContext';
import { EntraAuthProvider, useEntraAuth } from './contexts/EntraAuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { WorkoutTypesProvider } from './contexts/WorkoutTypesContext';
import apiClient from './services/api';
import { logger } from './utils/logger';
import { loadUserProfileSync, saveUserProfile } from './utils/userProfile';

// Navigation Components
const Navigation: React.FC = () => {
  const { state, logout } = useAuth();
  const {
    isAuthenticated: isEntraAuthenticated,
    user: entraUser,
    logout: entraLogout,
    getAccessToken,
  } = useEntraAuth();
  const { showError } = useNotification();
  const { isDebugMode } = useDebug();
  const navigate = useNavigate();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // Initialize API client with Entra token getter
  useEffect(() => {
    apiClient.setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Listen for backend connection errors
  useEffect(() => {
    const handleBackendError = (event: CustomEvent) => {
      const { type, message } = event.detail;
      logger.error('Backend connection error', new Error(message), { type, detail: event.detail });

      let userMessage = message;
      if (type === 'CONNECTION_REFUSED') {
        userMessage =
          'Cannot connect to backend server. Please check if the server is running on localhost:3000.';
      } else if (type === 'DNS_ERROR') {
        userMessage = 'Network error. Please check your internet connection.';
      } else if (type === 'TIMEOUT') {
        userMessage = 'Backend server is not responding. Please try again later.';
      } else if (type === 'SERVER_ERROR') {
        userMessage = 'Backend server error. Please try again later.';
      }

      showError(userMessage);
    };

    window.addEventListener('backend-connection-error', handleBackendError as EventListener);

    return () => {
      window.removeEventListener('backend-connection-error', handleBackendError as EventListener);
    };
  }, [showError]);

  // Listen for authentication errors and redirect to login
  useEffect(() => {
    const handleAuthError = (event: CustomEvent) => {
      const detail = event.detail;
      logger.error('Authentication error', new Error('Auth session expired'), { detail });

      // Logout from both Entra and Intervals.icu
      logout();
      entraLogout();

      // Show error message
      showError('Your session has expired. Please sign in again.');

      // Redirect to login page after a brief delay
      setTimeout(() => {
        window.location.href = '/signin';
      }, 1500);
    };

    window.addEventListener('auth-error', handleAuthError as EventListener);

    return () => {
      window.removeEventListener('auth-error', handleAuthError as EventListener);
    };
  }, [logout, entraLogout, showError]);

  const handleOpenProfileModal = () => {
    // Load current profile when opening modal
    const currentProfile = loadUserProfileSync();
    setUserProfile(currentProfile);
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  // Get user initials from Entra user data or Intervals.icu athlete name
  const getUserInitials = (): string => {
    const name = entraUser?.name || state.tokenData?.athlete?.name;
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    return 'U'; // User
  };

  // Get user display name from Entra user or Intervals.icu athlete
  const getUserDisplayName = (): string => {
    return entraUser?.name || entraUser?.username || state.tokenData?.athlete?.name || 'User';
  };

  const handleSaveProfile = async (profile: UserProfile) => {
    // Save to backend and localStorage
    await saveUserProfile(profile);
    setUserProfile(profile);
    logger.info('User profile saved successfully');

    // NOTE: Profile drawer stays open after save - user can continue editing
    // or close it manually. The ProfileDrawer shows its own success notification.
  };

  if (!isEntraAuthenticated) {
    return null;
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 },
            }}
            role="button"
            aria-label="Go to dashboard"
          >
            <img
              src="/icons/PacePilot_icon - 128.png"
              alt="PacePilot"
              style={{
                width: '40px',
                height: '40px',
                marginRight: '8px',
              }}
            />
            <Typography
              variant="h1"
              component="div"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2.125rem' },
              }}
            >
              PacePilot
            </Typography>
          </Box>

          {/* Connection Status */}
          <Box sx={{ mr: 2 }}>
            <ConnectionStatus variant="chip" />
          </Box>

          {/* User Name - Full name on md+, initials on sm, hidden on xs */}
          <Typography
            variant="body1"
            color="inherit"
            sx={{
              mr: 1,
              display: { xs: 'none', sm: 'block', md: 'block' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'none', md: 'inline' } }}>
              {getUserDisplayName()}
            </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline', md: 'none' } }}>
              {getUserInitials()}
            </Box>
          </Typography>

          {/* Profile Icon with Debug Tooltip */}
          <Tooltip
            title={
              isDebugMode ? (
                <Box sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  <Box>
                    <strong>User ID:</strong> {entraUser?.localAccountId || 'N/A'}
                  </Box>
                  <Box>
                    <strong>OID:</strong> {entraUser?.idTokenClaims?.oid || 'N/A'}
                  </Box>
                  <Box>
                    <strong>Email:</strong> {entraUser?.username || 'N/A'}
                  </Box>
                  <Box>
                    <strong>Name:</strong> {entraUser?.name || 'N/A'}
                  </Box>
                  <Box>
                    <strong>Tenant:</strong> {entraUser?.tenantId || 'N/A'}
                  </Box>
                  <Box>
                    <strong>Profile:</strong> {userProfile?.userId || 'Not loaded'}
                  </Box>
                  <Box>
                    <strong>Intervals:</strong> {userProfile?.intervalsConnected ? '✓' : '✗'}
                  </Box>
                </Box>
              ) : (
                'Open profile settings'
              )
            }
            arrow
            placement="bottom"
          >
            <IconButton
              onClick={handleOpenProfileModal}
              color="inherit"
              size="large"
              aria-label="Open profile settings"
            >
              <Person />
            </IconButton>
          </Tooltip>

          <IconButton
            color="inherit"
            onClick={() => {
              logout();
              entraLogout();
            }}
            aria-label="Logout from application"
          >
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Settings Drawer */}
      <ProfileDrawer
        isOpen={isProfileModalOpen}
        onClose={handleCloseProfileModal}
        onSave={handleSaveProfile}
        initialProfile={userProfile || undefined}
      />
    </>
  );
};

// Onboarding Wizard Route Component
const OnboardingWizardRoute: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [isNavigating, setIsNavigating] = useState(false);

  const handleComplete = async (profile: UserProfile) => {
    try {
      // Save profile to backend and localStorage
      await saveUserProfile(profile);
      logger.info('Onboarding complete - profile saved');
      showSuccess('Welcome to PacePilot! Your profile has been set up successfully.');

      // Mark onboarding as complete
      localStorage.setItem('pacepilot_onboarding_complete', 'true');

      // Navigate to dashboard
      setIsNavigating(true);
      window.location.href = '/';
    } catch (error) {
      logger.error('Error saving onboarding profile', error);
      showError('Failed to save your profile. Please try again.');
    }
  };

  const handleSkip = () => {
    logger.info('User skipped onboarding');
    localStorage.setItem('pacepilot_onboarding_complete', 'true');
    showSuccess('You can set up your profile later from the profile menu.');
    setIsNavigating(true);
    window.location.href = '/';
  };

  if (isNavigating) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Typography>Loading your dashboard...</Typography>
      </Box>
    );
  }

  return <OnboardingWizard onComplete={handleComplete} onSkip={handleSkip} />;
};

// Protected Route with Onboarding Check
const ProtectedRouteWithOnboarding: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated: isEntraAuthenticated } = useEntraAuth();
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);

  useEffect(() => {
    const checkOnboarding = async () => {
      // Check if onboarding was completed
      const onboardingComplete = localStorage.getItem('pacepilot_onboarding_complete');

      if (onboardingComplete === 'true') {
        setNeedsOnboarding(false);
        return;
      }

      // Check if user has a profile
      try {
        const profile = loadUserProfileSync();

        // Consider onboarding needed if:
        // 1. No profile exists at all
        // 2. Profile exists but has default values (never been customized)
        const hasCustomizedProfile =
          profile && (profile.thresholdPaceSec !== 240 || profile.weeklyMileage !== 50);

        setNeedsOnboarding(!hasCustomizedProfile);
      } catch (error) {
        logger.error('Error checking onboarding status', error);
        setNeedsOnboarding(false);
      }
    };

    if (isEntraAuthenticated) {
      checkOnboarding();
    }
  }, [isEntraAuthenticated]);

  if (!isEntraAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  if (needsOnboarding === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// Main App Content
const AppContent: React.FC = () => {
  const { state, checkAuth } = useAuth();
  const { isAuthenticated: isEntraAuthenticated, getAccessToken } = useEntraAuth();
  const { inProgress, accounts } = useMsal();
  const [isApiReady, setIsApiReady] = useState(false);
  const hasCheckedAuthRef = useRef(false);

  // Configure API client with Entra token getter - only after MSAL initialization is complete
  useEffect(() => {
    logger.debug('[App] Token getter setup effect', {
      inProgress,
      isEntraAuthenticated,
      accountsLength: accounts.length,
    });

    if (inProgress === 'none') {
      // If user is authenticated, set up token getter and wait for account to be available
      if (isEntraAuthenticated && accounts.length > 0) {
        logger.debug('[App] Setting up API client with token getter');
        apiClient.setAccessTokenGetter(getAccessToken);
        setIsApiReady(true);
        logger.debug('[App] API client ready');

        // Now that Entra auth is complete and API is ready, check Intervals.icu connection
        // Only call checkAuth once per session to prevent multiple /intervals/status requests
        if (!hasCheckedAuthRef.current) {
          hasCheckedAuthRef.current = true;
          checkAuth();
        }
      } else if (!isEntraAuthenticated) {
        // Not authenticated - allow render to redirect to signin
        logger.debug('[App] Not authenticated, setting API ready to allow redirect');
        setIsApiReady(true);
        // Reset the ref when user logs out so checkAuth runs again on next login
        hasCheckedAuthRef.current = false;
      } else {
        logger.debug('[App] Authenticated but accounts not loaded yet, waiting...');
      }
      // If authenticated but accounts not loaded yet, keep waiting
    }
  }, [getAccessToken, inProgress, accounts, isEntraAuthenticated, checkAuth]);

  // Wait for MSAL to finish initializing
  if (!isApiReady) {
    return (
      <LoadingAnimation
        fullPage
        message="Initializing authentication..."
        ariaLabel="Loading authentication"
      />
    );
  }

  return (
    <Router>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Navigation />

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pb: 2, // Minimal padding - toolbar is hidden by default
          }}
        >
          {/* Global Connection Status Banner */}
          <ConnectionStatus variant="banner" showDetails />

          <Routes>
            {/* Entra ID Sign In */}
            <Route
              path="/signin"
              element={isEntraAuthenticated ? <Navigate to="/" replace /> : <EntraSignIn />}
            />

            {/* Onboarding Wizard */}
            <Route
              path="/onboarding"
              element={
                !isEntraAuthenticated ? (
                  <Navigate to="/signin" replace />
                ) : (
                  <OnboardingWizardRoute />
                )
              }
            />

            {/* Intervals.icu Connection - Optional */}
            <Route
              path="/connect-intervals"
              element={
                !isEntraAuthenticated ? (
                  <Navigate to="/signin" replace />
                ) : state.isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <Login />
                )
              }
            />

            <Route path="/auth/intervals/callback" element={<OAuthPopupCallback />} />
            <Route path="/auth/callback" element={<OAuthPopupCallback />} />

            <Route
              path="/"
              element={
                <ProtectedRouteWithOnboarding>
                  <WorkoutTypesProvider>
                    <Dashboard />
                  </WorkoutTypesProvider>
                </ProtectedRouteWithOnboarding>
              }
            />

            {/* Clothing Sets Manager */}
            <Route
              path="/clothing-sets"
              element={
                <ProtectedRouteWithOnboarding>
                  <Box sx={{ p: 3 }}>
                    <ClothingSetManager />
                  </Box>
                </ProtectedRouteWithOnboarding>
              }
            />

            {/* Workout Library */}
            <Route
              path="/workout-library"
              element={
                <ProtectedRouteWithOnboarding>
                  <WorkoutLibrary />
                </ProtectedRouteWithOnboarding>
              }
            />

            {/* Workout Detail */}
            <Route
              path="/workout-library/:workoutId"
              element={
                <ProtectedRouteWithOnboarding>
                  <WorkoutDetail />
                </ProtectedRouteWithOnboarding>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>

        {/* Bottom Toolbar - show when Entra authenticated */}
        {isEntraAuthenticated && <BottomToolbar />}
      </Box>
    </Router>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <CustomThemeProvider>
      <NotificationProvider>
        <DebugProvider>
          <EntraAuthProvider>
            <AuthProvider>
              <LocationProvider>
                <AppContent />
                <VersionUpdateNotification />
                <InstallPrompt />
                <DebugOverlay />
              </LocationProvider>
            </AuthProvider>
          </EntraAuthProvider>
        </DebugProvider>
      </NotificationProvider>
    </CustomThemeProvider>
  );
};

export default App;
